/**
 * NIKKOR Z DX 16-50mm f/3.5-6.3 VR — the Z fc kit zoom.
 *
 * This is deliberately a data file. Adding a 28mm f/2.8 SE or a 50-250mm
 * later means adding an entry here, not touching resolve().
 */

import { snapToFNumber } from './dials'

export interface Lens {
  id: string
  name: string
  shortName: string
  minFocal: number
  maxFocal: number
  hasVr: boolean
  /** Claimed VR benefit in stops, for static subjects only. */
  vrStops: number
  /** Max (widest) aperture at sampled focal lengths — interpolated between. */
  maxApertureCurve: Array<{ focal: number; f: number }>
  /** Min (narrowest) aperture at the extremes. */
  minApertureCurve: Array<{ focal: number; f: number }>
}

export const KIT_16_50: Lens = {
  id: 'nikkor-z-dx-16-50-f3.5-6.3-vr',
  name: 'NIKKOR Z DX 16-50mm f/3.5-6.3 VR',
  shortName: '16-50mm kit',
  minFocal: 16,
  maxFocal: 50,
  hasVr: true,
  vrStops: 4.5,
  maxApertureCurve: [
    { focal: 16, f: 3.5 },
    { focal: 24, f: 4.5 },
    { focal: 35, f: 5.6 },
    { focal: 50, f: 6.3 },
  ],
  minApertureCurve: [
    { focal: 16, f: 22 },
    { focal: 50, f: 40 },
  ],
}

/**
 * NIKKOR Z DX 24mm f/1.7 — fast prime, 36mm equivalent. No VR: on the Z fc
 * (no in-body stabilisation) the bare handhold rule applies. f/1.7–f/11,
 * 0.18m close focus.
 */
export const PRIME_24_17: Lens = {
  id: 'nikkor-z-dx-24-f1.7',
  name: 'NIKKOR Z DX 24mm f/1.7',
  shortName: '24mm f/1.7',
  minFocal: 24,
  maxFocal: 24,
  hasVr: false,
  vrStops: 0,
  maxApertureCurve: [{ focal: 24, f: 1.7 }],
  minApertureCurve: [{ focal: 24, f: 11 }],
}

/** Picker order; the first entry is also the default for new visitors. */
export const LENSES: Lens[] = [PRIME_24_17, KIT_16_50]

export const DEFAULT_LENS: Lens = LENSES[0]

export const LENS_BY_ID = new Map(LENSES.map((l) => [l.id, l]))

/** A prime has exactly one focal length — no zoom ring to set. */
export function isPrime(lens: Lens): boolean {
  return lens.minFocal === lens.maxFocal
}

/** Keeps a stored or inherited focal length legal on the given lens. */
export function clampFocal(lens: Lens, focal: number): number {
  return Math.min(lens.maxFocal, Math.max(lens.minFocal, focal))
}

/** Nikon DX crop factor — drives the full-frame-equivalent field of view. */
export const DX_CROP_FACTOR = 1.5

function interpolateStops(curve: Array<{ focal: number; f: number }>, focal: number): number {
  const first = curve[0]
  const last = curve[curve.length - 1]
  if (focal <= first.focal) return first.f
  if (focal >= last.focal) return last.f

  for (let i = 0; i < curve.length - 1; i++) {
    const a = curve[i]
    const b = curve[i + 1]
    if (focal >= a.focal && focal <= b.focal) {
      // Interpolate in stops (log2 of the f-number), not linearly — that's how
      // aperture actually scales.
      const t = (focal - a.focal) / (b.focal - a.focal)
      const stopsA = Math.log2(a.f)
      const stopsB = Math.log2(b.f)
      return Math.pow(2, stopsA + t * (stopsB - stopsA))
    }
  }
  return last.f
}

/** Widest aperture physically available at this zoom position. */
export function maxApertureAt(lens: Lens, focal: number): number {
  return snapToFNumber(interpolateStops(lens.maxApertureCurve, focal))
}

/** Narrowest aperture available at this zoom position. */
export function minApertureAt(lens: Lens, focal: number): number {
  return snapToFNumber(interpolateStops(lens.minApertureCurve, focal))
}

/** Full-frame equivalent focal length, for people used to 35mm numbers. */
export function equivalentFocal(focal: number): number {
  return Math.round(focal * DX_CROP_FACTOR)
}

/**
 * The classic reciprocal rule, corrected for the DX sensor: you need roughly
 * 1/(focal × 1.5) to avoid your own handshake showing up.
 */
export function handheldThresholdSeconds(focal: number): number {
  return 1 / (focal * DX_CROP_FACTOR)
}
